// 전체 매핑 reset 확인 모달 (2026-05-18 운영자 결정).
//
// 동작:
//   - "RESET" 문자열을 정확히 타이핑해야 확인 버튼 활성.
//   - 사유 입력 (optional) textarea.
//   - 비가역 작업이므로 ESC / 백드롭 클릭으로 닫히지 않음.
//   - 확인 클릭 후 loading 상태 — 중복 클릭 방지.
//   - 성공 후 onSuccess 콜백 (부모가 토스트 + invalidate 처리).

import { useCallback, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { resetMappings } from '../../api/sectorTaxonomyApi';

interface Props {
  onClose: () => void;
  onSuccess: (deletedCount: number) => void;
  onError: (message: string) => void;
}

const CONFIRM_PHRASE = 'RESET';

export function MappingResetModal({
  onClose,
  onSuccess,
  onError,
}: Props): JSX.Element {
  const [confirmInput, setConfirmInput] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const confirmed = confirmInput === CONFIRM_PHRASE;

  const handleSubmit = useCallback(async () => {
    if (!confirmed || busy) return;
    setBusy(true);
    try {
      const resp = await resetMappings({ resetReason: reason.trim() });
      onSuccess(resp.mappingCountBefore);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'reset_failed';
      onError(msg);
    } finally {
      setBusy(false);
    }
  }, [busy, confirmed, onError, onSuccess, reason]);

  // 백드롭 클릭 / ESC 모두 무시 — 실수 차단. 닫기는 "취소" 버튼만.
  return (
    <div
      className="tx-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="tx-reset-modal-title"
      data-testid="mapping-reset-modal"
    >
      <div className="tx-modal" style={{ width: 'min(520px, 100%)' }}>
        <h3 className="tx-modal__title" id="tx-reset-modal-title">
          확정 매핑 reset
        </h3>
        <div className="tx-modal__body">
          <p style={{ margin: 0 }}>
            <strong>확정 (<code>confirmed</code>) 상태</strong>의{' '}
            <code>sector_mapping</code> 만 삭제됩니다.{' '}
            <code>pending_review</code> / <code>rejected</code> /{' '}
            <code>superseded</code> 는 <strong>보존</strong>.{' '}
            <strong>이 작업은 되돌릴 수 없습니다.</strong>
          </p>
          <p style={{ marginTop: 8, marginBottom: 0 }} className="pca-muted">
            reset 후 LLM Type-A 재실행으로 새 confirmed 매핑을 생성하세요.
          </p>
        </div>

        <label
          htmlFor="tx-reset-confirm"
          style={{ display: 'block', fontSize: 12, marginBottom: 4, marginTop: 12 }}
        >
          확인을 위해 <code>{CONFIRM_PHRASE}</code> 를 입력하세요 (필수)
        </label>
        <input
          id="tx-reset-confirm"
          className="pca-input"
          type="text"
          value={confirmInput}
          onChange={(e) => setConfirmInput(e.target.value)}
          placeholder={CONFIRM_PHRASE}
          autoComplete="off"
          autoFocus
          disabled={busy}
          data-testid="mapping-reset-confirm-input"
          style={{ width: '100%' }}
        />

        <label
          htmlFor="tx-reset-reason"
          style={{ display: 'block', fontSize: 12, marginBottom: 4, marginTop: 12 }}
        >
          사유 (선택)
        </label>
        <textarea
          id="tx-reset-reason"
          className="pca-input"
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="예: Type-A v2 prompt 재실행 대비 전체 매핑 정리"
          disabled={busy}
          data-testid="mapping-reset-reason"
          style={{ width: '100%' }}
        />

        <div className="tx-modal__actions">
          <button
            type="button"
            className="pca-btn"
            onClick={onClose}
            disabled={busy}
            data-testid="mapping-reset-cancel"
          >
            취소
          </button>
          <button
            type="button"
            className="pca-btn pca-btn--danger"
            onClick={handleSubmit}
            disabled={!confirmed || busy}
            data-testid="mapping-reset-submit"
          >
            {busy ? (
              <Loader2 size={14} strokeWidth={1.75} className="pca-spin" />
            ) : null}
            확정 매핑 reset 실행
          </button>
        </div>
      </div>
    </div>
  );
}
